import React, { createContext, useContext, useState, useEffect } from 'react';
import fetchJson from '../hooks/fetchJson';
import { subscribeInactive } from '../utils/inactiveBus';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        const currentTime = Date.now() / 1000;

        if (payload.exp < currentTime) {
          logout();
          return;
        }

        if (!['admin', 'owner', 'staff'].includes(payload.role)) {
          throw new Error('Invalid user role');
        }

        setUser({
          id: payload.id,
          username: payload.username,
          role: payload.role,
          ownerId: payload.ownerId || payload.id,
          staffId: payload.staffId
        });
        setToken(storedToken);

        if (payload.role === 'owner') {
          try {
            const { data, error, status } = await fetchJson('/api/owner/data', {
              headers: { 'Authorization': `Bearer ${storedToken}` }
            });
            if (error) {
              if (status === 403 && String(error).toLowerCase().includes('inactive')) {
                // Mark account as inactive so the app can render a special screen
                setOwnerData({ isactive: false });
                setInactive(true);
              } else {
                throw new Error(error);
              }
            } else {
              setOwnerData(data);
              setInactive(false);
            }
          } catch (err) {
            console.error('Error fetching owner data:', err);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Token validation failed:', err);
        logout();
      }
    };

    validateToken();
    // Subscribe to global inactive notifications (e.g., staff or owner 403)
    const unsubscribe = subscribeInactive(() => setInactive(true));
    return () => unsubscribe();
  }, []);

  const login = async (username, password, role) => {
    try {
      const endpoint = `/api/auth/${role}-login`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, role })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      const payload = JSON.parse(atob(data.token.split('.')[1]));
      if (!['admin', 'owner', 'staff'].includes(payload.role)) {
        throw new Error('Invalid user role');
      }

      const userWithStaffId = payload.role === 'staff' 
        ? { 
          username: data.user.username, 
          role: data.user.role, 
          ...(data.user.id && { id: data.user.id }), 
          ...(payload.ownerId && { ownerId: payload.ownerId }), 
          staffId: data.staffId 
        } 
        : {
          username: data.user.username,
          role: data.user.role,
          ...(data.user.id && { id: data.user.id }),
          ...(payload.ownerId && { ownerId: payload.ownerId })
        };

      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(userWithStaffId);

      if (payload.role === 'owner') {
        const { data: ownerInfo, error, status } = await fetchJson('/api/owner/data', {
          headers: { 'Authorization': `Bearer ${data.token}` }
        });
        if (error) {
          if (status === 403 && String(error).toLowerCase().includes('inactive')) {
            setOwnerData({ isactive: false });
          } else {
            throw new Error(error);
          }
        } else {
          setOwnerData(ownerInfo);
        }
      }

      return userWithStaffId;
    } catch (err) {
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setOwnerData(null);
    setInactive(false);
  };

  const isAuthenticated = () => !!token && !!user;

  const hasRole = (role) => user && user.role === role;

  return (
    <AuthContext.Provider value={{
      user,
      ownerData,
      token,
      loading,
      inactive,
      login,
      logout,
      isAuthenticated,
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);